<template>
  <div class="play-header" :class="`play-header--${categoryColor}`">
    <div class="play-header__inner">
      <div class="top">
        <button type="button" @click="onBack" class="back-btn"><span class="material-icons" aria-hidden="true">play_arrow</span> Back</button>
        <p class="timer" :class="`timer--${categoryColor}`" v-html="formattedDurationSecs" />
        <p class="pb">{{ formattedPersonalBestSecs }}</p>
      </div>
      <div class="bottom">
        <p v-if="promptPrefix" class="prompt-prefix">{{ promptPrefixComputed }}</p>
        <h3 class="prompt" :class="`${promptModifier}`" v-html="currentTilePromptComputed" />
      </div>
      <button v-if="playState === 'READY'" type="button" @click="emitEventPlay" class="play-btn" :class="`play-btn--${categoryColor}`">
        <span class="sr-only">Play</span><span class="material-icons" aria-hidden="true">play_arrow</span>
      </button>

      <!-- <button type="button" @click="emitEventShuffleBoard">Shuffle</button> -->
      <!-- <button type="button" @click="emitEventResetBoard">Reset</button> -->
      <!-- <p>{{ playState }}</p> -->
    </div>
  </div>
</template>

<script>
import {createNamespacedHelpers} from 'vuex';
import {INCORRECT_CLICK_PENALTY_DURATION_SECS, PLAY_STATES, TIMEOUT_DURATION_SECS, RANDOM_PROMPT_DELIMITER} from './../constants/globalConstants';
import getFormattedDurationSecs from './../helpers/getFormattedDurationSecs';
import PlayTile from './PlayTile';
import zzfx from './../helpers/zzfx';
import {TIMER_SECONDS_TICK_SFX} from './../constants/sfx';
import getRandomItemFromArray from './../helpers/getRandomItemFromArray';

const userVuexModule = createNamespacedHelpers('userVuexModule');

export default {
  name: 'PlayHeader',
  components: {},
  props: {
    categoryColor: {
      type: String,
      default: '',
    },
    currentTilePrompt: {
      type: String,
      default: '',
    },
    numIncorrectClicks: {
      type: Number,
      default: 0,
    },
    personalBestTotalDurationMs: {
      type: Number,
      default: null,
    },
    playState: {
      type: String,
      default: '',
    },
    playStateCssModifier: {
      type: String,
      default: '',
    },
    promptPrefix: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      elapsedDurationMs: 0,
      raf: null,
      startMs: 0,
      timerIsRunning: false,
      secondsSetTimeout: null,
    };
  },
  watch: {
    playState: function() {
      if (this.playState === PLAY_STATES.PLAYING.CONSTANT) {
        this.startTimer();
      } else if (this.playState === PLAY_STATES.COMPLETED.CONSTANT) {
        this.stopTimer();
        this.$emit('timer-stopped', this.elapsedDurationMs);
      }
    },
  },
  computed: {
    ...userVuexModule.mapGetters(['getPlaySoundEffects']),
    promptPrefixComputed: function() {
      if (this.playState === PLAY_STATES.READY.CONSTANT) {
        return this.promptPrefix;
      } else if (this.playState === PLAY_STATES.COUNTING_DOWN.CONSTANT) {
        return this.promptPrefix;
      } else if (this.playState === PLAY_STATES.PLAYING.CONSTANT) {
        return this.promptPrefix;
      } else {
        return '';
      }
    },
    currentTilePromptComputed: function() {
      if (this.playState === PLAY_STATES.READY.CONSTANT) {
        return '&nbsp;';
      } else if (this.playState === PLAY_STATES.COUNTING_DOWN.CONSTANT) {
        return '&nbsp;';
      } else if (this.playState === PLAY_STATES.PLAYING.CONSTANT) {
        const arrayOfPrompts = this.currentTilePrompt
          .split(RANDOM_PROMPT_DELIMITER)
          .filter(item => item.trim() !== '')
          .map(item => item.trim());
        if (arrayOfPrompts.length === 1) {
          return arrayOfPrompts[0];
        } else {
          return getRandomItemFromArray(arrayOfPrompts);
        }
      } else {
        return '';
      }
    },
    promptModifier: function() {
      if (this.playState === PLAY_STATES.READY.CONSTANT) {
        return 'prompt--ready';
      } else if (this.playState === PLAY_STATES.COUNTING_DOWN.CONSTANT) {
        return 'prompt--counting-down';
      } else if (this.playState === PLAY_STATES.PLAYING.CONSTANT) {
        return 'prompt--playing';
      } else {
        return '';
      }
    },
    formattedDurationSecs: function() {
      const totalDurationMs = this.elapsedDurationMs + this.penaltyDurationMs;
      return getFormattedDurationSecs(totalDurationMs, true);
    },
    formattedPersonalBestSecs: function() {
      return `PB: ${getFormattedDurationSecs(this.personalBestTotalDurationMs, false)}`;
    },
    penaltyDurationMs: function() {
      return INCORRECT_CLICK_PENALTY_DURATION_SECS * 1000 * this.numIncorrectClicks;
    },
    timeoutDurationMs: function() {
      return TIMEOUT_DURATION_SECS * 1000;
    },
  },
  methods: {
    emitEventShuffleBoard: function() {
      this.$emit('shuffle-board');
    },
    emitEventPlay: function() {
      this.$emit('play');
    },
    emitEventResetBoard: function() {
      this.$emit('reset-board');
    },
    onBack: function() {
      this.$router.go(-1);
    },
    startTimer: function() {
      this.timerIsRunning = true;
      this.startMs = Date.now();
      this.raf = requestAnimationFrame(this.updateElapsedMs);
      if (this.getPlaySoundEffects) {
        this.secondsTick();
      }
    },
    stopTimer: function() {
      this.timerIsRunning = false;
      cancelAnimationFrame(this.raf);
      clearTimeout(this.secondsSetTimeout);
    },
    resetTimer: function() {
      this.elapsedDurationMs = 0;
      this.startMs = Date.now();
    },
    updateElapsedMs: function() {
      this.elapsedDurationMs = Date.now() - this.startMs;
      if (this.elapsedDurationMs >= this.timeoutDurationMs) {
        this.stopTimer();
        this.elapsedDurationMs = this.timeoutDurationMs;
        this.timeout();
      } else {
        this.raf = requestAnimationFrame(this.updateElapsedMs);
      }
    },
    secondsTick: function() {
      if (this.timerIsRunning) {
        this.secondsSetTimeout = setTimeout(() => {
          if (this.getPlaySoundEffects) zzfx(...TIMER_SECONDS_TICK_SFX);
          this.secondsTick();
        }, 1000);
      }
    },
    timeout: function() {
      this.raf = null;
      this.$emit('timeout');
    },
  },
  beforeDestroy() {
    this.stopTimer();
  },
};
</script>

<style lang="scss" scoped>
.play-header {
  background-color: var(--color-header-background);
  border-bottom: 1px solid var(--color-header-border-bottom);
  color: var(--color-header-text);
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  height: 12rem;
  justify-content: stretch;
  min-height: var(--min-play-header-height);
  z-index: 1;

  &--yellow {
    border-bottom-color: var(--color-yellow);
  }

  &--orange {
    border-bottom-color: var(--color-orange);
  }

  &--pink {
    border-bottom-color: var(--color-pink);
  }

  &--violet {
    border-bottom-color: var(--color-violet);
  }

  &--blue {
    border-bottom-color: var(--color-blue);
  }

  &--green {
    border-bottom-color: var(--color-green);
  }

  &--teal {
    border-bottom-color: var(--color-teal);
  }

  &--brown {
    border-bottom-color: var(--color-brown);
  }

  &__inner {
    align-items: center;
    display: flex;
    flex-direction: column;
    height: 100%;
    justify-content: space-between;
    margin: 0 auto;
    max-width: var(--max-width-site);
    padding: var(--padding-s);
    width: 100%;
  }
}

.play-btn {
  background-color: transparent;
  left: 50%;
  position: absolute;
  text-shadow: 0px 0px 20px hsla(0, 0%, 0%, 0.9);
  top: 50%;
  transform: translate(-50%, -50%);

  &--yellow {
    color: var(--color-yellow);
  }

  &--orange {
    color: var(--color-orange);
  }

  &--pink {
    color: var(--color-pink);
  }

  &--violet {
    color: var(--color-violet);
  }

  &--blue {
    color: var(--color-blue);
  }

  &--green {
    color: var(--color-green);
  }

  &--teal {
    color: var(--color-teal);
  }

  &--brown {
    color: var(--color-brown);
  }

  .material-icons {
    font-size: 25rem;
  }
}

.top {
  align-items: flex-start;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 0.8rem;
  width: 100%;
}

.back-btn {
  align-items: center;
  background-color: transparent;
  border-radius: 0;
  display: flex;
  flex-direction: row;
  flex-shrink: 0;
  font-size: 1.2rem;
  font-weight: 300;
  justify-content: flex-start;
  line-height: 1;
  margin: 0;
  min-width: 9rem;
  opacity: 0.5;
  padding: 0;
  text-align: left;

  &:hover {
    opacity: 1;
  }

  .material-icons {
    font-size: 1.7rem;
    transform: scaleX(-1);
  }
}

.timer {
  /* border: 1px solid green; */
  font-family: 'Roboto Mono', monospace;
  font-size: 1.6rem;
  font-weight: 700;
  line-height: 1;
  margin: 0;
  padding: 0;
  text-align: center;
  width: 100%;
  text-transform: uppercase;

  &--yellow {
    color: var(--color-yellow);
  }

  &--orange {
    color: var(--color-orange);
  }

  &--pink {
    color: var(--color-pink);
  }

  &--violet {
    color: var(--color-violet);
  }

  &--blue {
    color: var(--color-blue);
  }

  &--green {
    color: var(--color-green);
  }

  &--teal {
    color: var(--color-teal);
  }

  &--brown {
    color: var(--color-brown);
  }
}

.pb {
  /* border: 1px solid red; */
  flex-shrink: 0;
  font-size: 1.2rem;
  font-weight: 300;
  line-height: 1;
  margin: 0;
  min-width: 9rem;
  opacity: 0.5;
  padding: 0;
  text-align: right;
}

.bottom {
  margin-bottom: 0.8rem;
  width: 100%;
}

.prompt-prefix {
  font-size: 1.6rem;
  font-weight: 300;
  margin: 0 0 1rem 0;
  max-width: none;
  padding: 0;
  text-align: center;
}

.prompt {
  font-size: 2.1rem;
  font-weight: 700;
  line-height: 1;
  margin: 0;
  max-width: none;
  padding: 0;
  position: relative;
  text-align: center;
  width: 100%;

  @include breakpoint($mq-sm) {
    font-size: 2.2rem;
  }

  @include breakpoint($mq-sm-md) {
    font-size: 2.3rem;
  }

  @include breakpoint($mq-md) {
    font-size: 2.4rem;
  }

  @include breakpoint($mq-md-lg) {
    font-size: 2.5rem;
  }

  @include breakpoint($mq-lg) {
    font-size: 2.6rem;
  }

  @include breakpoint($mq-lg-xlg) {
    font-size: 2.7rem;
  }

  @include breakpoint($mq-xlg) {
    font-size: 2.8rem;
  }

  @include breakpoint($mq-xlg-xxlg) {
    font-size: 2.9rem;
  }

  @include breakpoint($mq-xxlg) {
    font-size: 3rem;
  }

  &--ready,
  &--counting-down {
    &::after {
      background-color: var(--text-color);
      bottom: 0;
      content: '';
      display: block;
      height: 0.2rem;
      left: 50%;
      max-width: 37rem;
      min-width: 23rem;
      position: absolute;
      transform: translateX(-50%);
      width: 33%;
    }
  }
}
</style>

<style lang="scss">
.timer__leading-zeros {
  opacity: 0.4;
}
</style>
